import { useState } from "react";
import { Dialog, type DialogProps } from "./Dialog";
import { Select } from "./Select";
import { Tabs, type TabItem } from "./Tabs";
import { cn } from "../lib/cn";

export interface TabbedDialogProps extends Omit<
  DialogProps,
  "children" | "size" | "bodyClassName"
> {
  tabsLabel: string;
  defaultTab: string;
  tab?: string;
  onTabChange?: (value: string) => void;
  tabs: readonly TabItem[];
  navigation?: "tabs" | "sidebar";
}

/**
 * The standard window for tabbed settings. Its size is fixed, so switching tabs
 * never resizes it: a tab that is too long scrolls inside the window.
 */
export function TabbedDialog({
  tabsLabel,
  defaultTab,
  tab,
  onTabChange,
  tabs,
  navigation = "tabs",
  className,
  ...dialog
}: TabbedDialogProps) {
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const activeTab = tab ?? selectedTab;
  const selectTab = (value: string) => {
    setSelectedTab(value);
    onTabChange?.(value);
  };
  if (navigation === "sidebar") {
    return (
      <Dialog
        {...dialog}
        className={cn(
          "h-[min(36rem,calc(100dvh-2rem))] max-w-4xl text-sm text-secondary",
          className,
        )}
        bodyClassName="overflow-hidden px-0 py-0"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 border-b border-border px-5 py-3 sm:hidden">
            <Select
              label="Settings category"
              value={activeTab}
              options={tabs.map(({ value, label }) => ({ value, label }))}
              onValueChange={selectTab}
            />
          </div>
          <Tabs
            label={tabsLabel}
            defaultValue={defaultTab}
            value={activeTab}
            onValueChange={selectTab}
            orientation="vertical"
            items={tabs.map((item) => ({
              ...item,
              content: (
                <>
                  <h2 className="mb-5 text-base font-semibold text-primary">{item.label}</h2>
                  {item.content}
                </>
              ),
            }))}
            className="flex min-h-0 flex-1"
            listClassName="mb-0 hidden w-44 shrink-0 flex-col gap-1 overflow-x-hidden overflow-y-auto border-b-0 border-r border-border px-3 py-4 sm:flex [&>button]:justify-items-start [&>button]:rounded-control [&>button]:border-0 [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-left [&>button[data-state=active]]:bg-surface-elevated [&>button[data-state=active]]:font-semibold"
            panelClassName="min-h-0 min-w-0 flex-1 overflow-y-auto px-5 py-5"
          />
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      {...dialog}
      className={cn("h-[min(36rem,calc(100dvh-2rem))] max-w-3xl text-sm text-secondary", className)}
      bodyClassName="overflow-hidden px-0 py-0"
    >
      <Tabs
        label={tabsLabel}
        defaultValue={defaultTab}
        value={tab}
        onValueChange={onTabChange}
        items={tabs}
        className="flex h-full min-h-0 flex-col"
        listClassName="mb-0 shrink-0 justify-start overflow-x-auto bg-transparent px-5 [&>button]:shrink-0 [&>button]:whitespace-nowrap [&>button]:px-3 [&>button]:py-3"
        panelClassName="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-5"
      />
    </Dialog>
  );
}
